import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import Modal from 'components/Modal';
import {
  applyCompanyJobRoleSync,
  applyCompanyTrainingModuleSync,
  previewCompanyJobRoleSync,
  previewCompanyTrainingModuleSync,
  rejectCompanyJobRoleSync,
  rejectCompanyTrainingModuleSync,
} from 'http/companyProvisioning';
import { getTrainingPartsImpactedSessions } from 'http/trainingParts';
import { resolveApiErrorMessage } from 'utils';
import { useSessionsImpactConfirm } from 'pages/TrainingModules/Edit/helpers/sessionsImpactConfirm';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  changeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginBottom: theme.spacing(0.75),
  },
  fieldChange: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginBottom: theme.spacing(1),
  },
  chipAdded: {
    backgroundColor: theme.palette.success.light,
  },
  chipRemoved: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  chipUpdated: {
    backgroundColor: theme.palette.warning.light,
  },
  empty: {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  text: {
    margin: 0,
  },
  label: {
    margin: 0,
    marginBottom: theme.spacing(0.5),
  },
}));

const formatPartLabel = item => `${item.code} ${item.version}.${item.revision || '0'}`;

const collectPendingUpgradeIds = preview => {
  if (!preview) {
    return [];
  }
  return (preview.upgrades || []).map(item => item.id);
};

const hasPendingUpgrades = preview => collectPendingUpgradeIds(preview).length > 0;

const collectScheduleImpactPartIds = preview => {
  if (!preview?.upgrades?.length) {
    return [];
  }

  const partIds = [];
  preview.upgrades.forEach(upgrade => {
    const parts = upgrade.metadata?.parts || [];
    parts.forEach(part => {
      const changes = part.partChanges || {};
      if (
        Object.prototype.hasOwnProperty.call(changes, 'availableTo') ||
        Object.prototype.hasOwnProperty.call(changes, 'validityMonthCount')
      ) {
        if (part.companyTrainingPartId) {
          partIds.push(part.companyTrainingPartId);
        }
      }
    });
  });

  return [...new Set(partIds)];
};

const formatVersionLabel = metadata => metadata?.fullVersionCode || `#${metadata?.systemJobRoleVersionId || ''}`;

const formatModuleLabel = metadata =>
  metadata?.code ? `${metadata.code} · ${metadata.name || ''}` : `#${metadata?.systemTrainingModuleId || ''}`;

const formatFieldValue = value => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'string' && value.includes('T') && !Number.isNaN(Date.parse(value))) {
    return new Date(value).toLocaleString();
  }
  return String(value);
};

const FileDiffSection = ({ title, files, chipClass, t, classes }) => {
  if (!files?.length) {
    return null;
  }
  return (
    <Box mb={1}>
      <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
        {title}
      </Typography>
      <Box className={classes.changeRow}>
        {files.map(file => (
          <Chip
            key={`${title}-${file.name}`}
            size="medium"
            className={chipClass}
            label={`${file.name}${file.documentSize ? ` · ${file.documentSize}b` : ''}`}
          />
        ))}
      </Box>
    </Box>
  );
};

FileDiffSection.propTypes = {
  title: PropTypes.string.isRequired,
  files: PropTypes.array,
  chipClass: PropTypes.string,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

const JobRoleUpgradeDetails = ({ item, t, classes }) => {
  const metadata = item.metadata || {};
  const versionChanges = metadata.versionChanges || {};
  const requirements = metadata.requirements || {};
  const added = requirements.added || [];
  const removed = requirements.removed || [];
  const updated = requirements.updated || [];
  const versionFields = Object.entries(versionChanges);

  return (
    <Box className={classes.section}>
      <Typography component="div" variant="subtitle1" className={classes.sectionTitle}>
        {t('companyProvisioning.upgradeVersion', 'Version')} {formatVersionLabel(metadata)}
      </Typography>

      {versionFields.length ? (
        <Box mb={1}>
          <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
            {t('companyProvisioning.versionFieldChanges', 'Version field changes')}
          </Typography>
          {versionFields.map(([field, value]) => (
            <Box key={field} className={classes.fieldChange}>
              <Chip size="medium" label={field} className={classes.chipUpdated} />
              <Typography component="div" variant="body2" className={classes.text}>
                {formatFieldValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {added.length ? (
        <Box mb={1}>
          <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
            {t('companyProvisioning.requirementsAdded', '{{count}} added', { count: added.length })}
          </Typography>
          <Box className={classes.changeRow}>
            {added.map(req => (
              <Chip
                key={`added-${req.systemTrainingModuleId}`}
                size="medium"
                className={classes.chipAdded}
                label={`TM #${req.systemTrainingModuleId}${req.sequence !== undefined ? ` · seq ${req.sequence}` : ''}${
                  req.status ? ` · ${req.status}` : ''
                }`}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      {removed.length ? (
        <Box mb={1}>
          <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
            {t('companyProvisioning.requirementsRemoved', '{{count}} removed', {
              count: removed.length,
            })}
          </Typography>
          <Box className={classes.changeRow}>
            {removed.map(req => (
              <Chip
                key={`removed-${req.systemTrainingModuleId}`}
                size="medium"
                className={classes.chipRemoved}
                label={`TM #${req.systemTrainingModuleId}${
                  req.companyTrainingModuleId ? ` → #${req.companyTrainingModuleId}` : ''
                }`}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      {updated.length ? (
        <Box mb={1}>
          <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
            {t('companyProvisioning.requirementsUpdated', '{{count}} updated', {
              count: updated.length,
            })}
          </Typography>
          <Box className={classes.changeRow}>
            {updated.map(req => (
              <Chip
                key={`updated-${req.systemTrainingModuleId}`}
                size="medium"
                className={classes.chipUpdated}
                label={`TM #${req.systemTrainingModuleId}${req.sequence !== undefined ? ` · seq ${req.sequence}` : ''}${
                  req.status ? ` · ${req.status}` : ''
                }`}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

JobRoleUpgradeDetails.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    metadata: PropTypes.object,
  }).isRequired,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

const TrainingModuleUpgradeDetails = ({ item, t, classes }) => {
  const metadata = item.metadata || {};
  const moduleChanges = Object.entries(metadata.moduleChanges || {});
  const moduleFiles = metadata.moduleFiles || {};
  const parts = metadata.parts || [];

  return (
    <Box className={classes.section}>
      <Typography component="div" variant="subtitle1" className={classes.sectionTitle}>
        {t('companyProvisioning.upgradeModule', 'Module')} {formatModuleLabel(metadata)}
      </Typography>

      {moduleChanges.length ? (
        <Box mb={1}>
          <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
            {t('companyProvisioning.moduleFieldChanges', 'Module field changes')}
          </Typography>
          {moduleChanges.map(([field, value]) => (
            <Box key={field} className={classes.fieldChange}>
              <Chip size="medium" label={field} className={classes.chipUpdated} />
              <Typography component="div" variant="body2" className={classes.text}>
                {formatFieldValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <FileDiffSection
        title={t('companyProvisioning.moduleFilesAdded', 'Module files added')}
        files={moduleFiles.added}
        chipClass={classes.chipAdded}
        t={t}
        classes={classes}
      />
      <FileDiffSection
        title={t('companyProvisioning.moduleFilesRemoved', 'Module files removed')}
        files={moduleFiles.removed}
        chipClass={classes.chipRemoved}
        t={t}
        classes={classes}
      />
      <FileDiffSection
        title={t('companyProvisioning.moduleFilesUpdated', 'Module files updated')}
        files={moduleFiles.updated}
        chipClass={classes.chipUpdated}
        t={t}
        classes={classes}
      />

      {parts.map(part => {
        const partChanges = Object.entries(part.partChanges || {});
        const partFiles = part.partFiles || {};
        return (
          <Box key={part.systemTrainingPartId} mb={1}>
            <Typography component="div" variant="caption" color="textSecondary" className={classes.label}>
              {t('companyProvisioning.upgradePart', 'Part')} {formatPartLabel(part)}
            </Typography>
            {partChanges.map(([field, value]) => (
              <Box key={`${part.systemTrainingPartId}-${field}`} className={classes.fieldChange}>
                <Chip size="medium" label={field} className={classes.chipUpdated} />
                <Typography component="div" variant="body2" className={classes.text}>
                  {formatFieldValue(value)}
                </Typography>
              </Box>
            ))}
            <FileDiffSection
              title={t('companyProvisioning.partFilesAdded', 'Part files added')}
              files={partFiles.added}
              chipClass={classes.chipAdded}
              t={t}
              classes={classes}
            />
            <FileDiffSection
              title={t('companyProvisioning.partFilesRemoved', 'Part files removed')}
              files={partFiles.removed}
              chipClass={classes.chipRemoved}
              t={t}
              classes={classes}
            />
            <FileDiffSection
              title={t('companyProvisioning.partFilesUpdated', 'Part files updated')}
              files={partFiles.updated}
              chipClass={classes.chipUpdated}
              t={t}
              classes={classes}
            />
          </Box>
        );
      })}
    </Box>
  );
};

TrainingModuleUpgradeDetails.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    metadata: PropTypes.object,
  }).isRequired,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

const CompanySyncPanel = ({ entityType, entityId, onSynced }) => {
  const { t } = useTranslation('common');
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openSessionsImpactConfirm = useSessionsImpactConfirm();

  const previewFn = entityType === 'jobRole' ? previewCompanyJobRoleSync : previewCompanyTrainingModuleSync;
  const applyFn = entityType === 'jobRole' ? applyCompanyJobRoleSync : applyCompanyTrainingModuleSync;
  const rejectFn = entityType === 'jobRole' ? rejectCompanyJobRoleSync : rejectCompanyTrainingModuleSync;

  const [previewState, loadPreview] = useAsyncFn(async () => previewFn(entityId), [entityId, previewFn]);

  const openPreviewModal = async () => {
    const result = await loadPreview();
    if (!result?.error) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => setIsModalOpen(false);

  const applyUpdates = async preview => {
    const result = await applyFn(entityId, {
      upgradeIds: collectPendingUpgradeIds(preview),
    });
    if (!result?.error) {
      setIsModalOpen(false);
      if (onSynced) {
        onSynced();
      }
      await loadPreview();
    }
    return result;
  };

  const [applyState, runApply] = useAsyncFn(async () => {
    const preview = previewState.value || (await loadPreview());
    if (preview?.error) {
      return preview;
    }

    if (entityType !== 'trainingModule') {
      return applyUpdates(preview);
    }

    const partIds = collectScheduleImpactPartIds(preview);
    if (!partIds.length) {
      return applyUpdates(preview);
    }

    const impacted = await getTrainingPartsImpactedSessions(partIds);
    if (impacted?.error || impacted?.statusCode) {
      return impacted;
    }

    if (!impacted?.items?.length) {
      return applyUpdates(preview);
    }

    return new Promise(resolve => {
      openSessionsImpactConfirm({
        sessions: impacted.items,
        onConfirm: async () => {
          const result = await applyUpdates(preview);
          resolve(result);
        },
        onCancel: () => resolve(undefined),
      });
    });
  }, [applyFn, entityId, entityType, loadPreview, onSynced, openSessionsImpactConfirm, previewState.value]);

  const [rejectState, runReject] = useAsyncFn(async () => {
    const preview = previewState.value || (await loadPreview());
    if (preview?.error) {
      return preview;
    }
    const result = await rejectFn(entityId, {
      upgradeIds: collectPendingUpgradeIds(preview),
    });
    if (!result?.error) {
      setIsModalOpen(false);
      if (onSynced) {
        onSynced();
      }
      await loadPreview();
    }
    return result;
  }, [entityId, loadPreview, onSynced, previewState.value, rejectFn]);

  const preview = previewState.value;
  const upgrades = preview?.upgrades || [];
  const hasUpdates = hasPendingUpgrades(preview);
  const errorMessage =
    preview?.error || applyState.value?.error || rejectState.value?.error
      ? resolveApiErrorMessage(preview?.message || applyState.value?.message || rejectState.value?.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        })
      : null;
  const busy = previewState.loading || applyState.loading || rejectState.loading;

  const modalActions = useMemo(() => {
    if (!hasUpdates) {
      return [
        {
          key: 'close',
          Component: () => (
            <Button variant="outlined" color="primary" onClick={closeModal} disabled={busy}>
              {t('buttons.close', 'Close')}
            </Button>
          ),
        },
      ];
    }

    return [
      {
        key: 'apply',
        Component: () => (
          <Button variant="contained" color="primary" onClick={runApply} disabled={busy}>
            {t('companyProvisioning.syncApply', 'Apply updates')}
          </Button>
        ),
      },
      {
        key: 'reject',
        Component: () => (
          <Button variant="outlined" color="secondary" onClick={runReject} disabled={busy}>
            {t('companyProvisioning.syncReject', 'Reject updates')}
          </Button>
        ),
      },
      {
        key: 'cancel',
        Component: () => (
          <Button variant="outlined" onClick={closeModal} disabled={busy}>
            {t('buttons.cancel', 'Cancel')}
          </Button>
        ),
      },
    ];
  }, [busy, hasUpdates, runApply, runReject, t]);

  return (
    <Box mt={2} mb={2}>
      <Typography variant="subtitle1">{t('companyProvisioning.syncTitle', 'Check for updates')}</Typography>
      <Box mt={1}>
        <Button variant="outlined" color="primary" onClick={openPreviewModal} disabled={busy}>
          {t('companyProvisioning.syncPreview', 'Preview updates')}
        </Button>
      </Box>
      {errorMessage && !isModalOpen ? (
        <Typography color="error" variant="body2">
          {errorMessage}
        </Typography>
      ) : null}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={t('companyProvisioning.syncPreviewTitle', 'Available updates')}
        size="medium"
        modalType="notification"
        actions={modalActions}
      >
        {errorMessage ? (
          <Typography component="div" color="error" variant="body2" className={classes.text}>
            {errorMessage}
          </Typography>
        ) : null}

        {hasUpdates ? (
          <Box>
            {upgrades.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 ? <Divider /> : null}
                {entityType === 'jobRole' ? (
                  <JobRoleUpgradeDetails item={item} t={t} classes={classes} />
                ) : (
                  <TrainingModuleUpgradeDetails item={item} t={t} classes={classes} />
                )}
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Typography component="div" variant="body2" className={classes.empty}>
            {t('companyProvisioning.noUpdates', 'No updates available')}
          </Typography>
        )}
      </Modal>
    </Box>
  );
};

CompanySyncPanel.propTypes = {
  entityType: PropTypes.oneOf(['jobRole', 'trainingModule']).isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSynced: PropTypes.func,
};

export default CompanySyncPanel;
